"use client";
import { Track } from "@/src/lib/tracking";

import React, { useCallback, useEffect, useRef, useState } from "react";
import { deviceDetector, setItem } from "utils";
import { ospThemes } from "@ospweb/sdk-ui";
import { EdgenModal } from "@components/EdgenModal";
import { LOCAL_STORAGE_KEYS } from "@config/constant";
import { useGlobalAppModalLimit } from "@hooks/useGlobalAppModalLimit";
import { useBridge } from "@/src/lib/bridge";
import { AppVersionUpdateModalContent } from "./AppVersionUpdateModalContent";
import { resolveAppVersionUpdateModal } from "./appVersionUpdatePolicy";
import { closeOtherModals, ModalStore } from "@store/modules/modal";
import { ANDROID_PLAY_STORE_URL } from "@config/nativeAppIdentity";

const GLOBAL_APP_MODAL_KEY_APP_VERSION_UPDATE = "app-version-update";

const APP_UPDATE_URL = {
  android: ANDROID_PLAY_STORE_URL,
  ios: "https://apps.apple.com/app/id6755764161",
} as const;

export const AppVersionUpdateModalContainer: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [canCloseUpgradeModal, setCanCloseUpgradeModal] = useState(true);
  const { openUrl } = useBridge();

  const closeModal = useCallback(
    (source?: string) => {
      if (source !== "remote-claim" && canCloseUpgradeModal) {
        setItem(
          LOCAL_STORAGE_KEYS.APP_VERSION_UPDATE_MODAL_LAST_SHOWN,
          Date.now(),
        );
      }
      ModalStore.appVersionUpdateModalVisible = false;
      setVisible(false);
    },
    [canCloseUpgradeModal],
  );

  const { tryOpen, isSuppressed } = useGlobalAppModalLimit(
    GLOBAL_APP_MODAL_KEY_APP_VERSION_UPDATE,
    {
      hideInAppChatModal: true,
      onRemoteClaim: () => closeModal("remote-claim"),
    },
  );
  const tryOpenRef = useRef(tryOpen);
  tryOpenRef.current = tryOpen;

  const openModal = useCallback(() => {
    setVisible((prev) => {
      if (prev) {
        return prev;
      }
      return true;
    });
    ModalStore.appVersionUpdateModalVisible = true;
    // 打开 APP 升级弹窗，关闭其他弹窗
    closeOtherModals();
  }, []);

  const handleUpdateNow = useCallback(() => {
    if (deviceDetector.isIOSApp()) {
      openUrl(APP_UPDATE_URL.ios);
      Track.log("info", "AppVersionUpdate", "Update now → iOS openUrl", {
        cause: String(APP_UPDATE_URL.ios),
      });
    } else if (deviceDetector.isAndroidApp()) {
      openUrl(APP_UPDATE_URL.android);
      Track.log("info", "AppVersionUpdate", "Update now → Android openUrl");
    } else {
      Track.log("warn", "AppVersionUpdate", "Update now → unknown platform");
    }
    if (canCloseUpgradeModal) {
      closeModal();
    }
  }, [canCloseUpgradeModal, closeModal, openUrl]);

  useEffect(() => {
    if (!deviceDetector.isApp()) {
      return;
    }

    let cancelled = false;

    const runCheck = async () => {
      try {
        const { shouldShow, canCloseUpgradeModal: canClose } =
          await resolveAppVersionUpdateModal();
        if (cancelled || !shouldShow) {
          return;
        }
        if (isSuppressed) {
          return;
        }
        setCanCloseUpgradeModal(canClose);
        const opened = tryOpenRef.current(openModal);
        if (!opened) {
        }
      } catch (error) {
        Track.log("warn", "AppVersionUpdateModalContainer", "check failed", {
          cause: String(error),
        });
      }
    };

    void runCheck();

    return () => {
      cancelled = true;
    };
  }, [isSuppressed, openModal]);

  if (!deviceDetector.isApp()) {
    return null;
  }

  return (
    <EdgenModal
      open={visible}
      onClose={() => closeModal()}
      centered
      title={null}
      footer={null}
      closable={false}
      maskClosable={false}
      width={320}
      containerClassName="rounded-4 overflow-hidden"
      styles={{
        mask: {
          backgroundColor: ospThemes.colors.background_clearcover,
        },
        content: {
          padding: 0,
          backgroundColor: ospThemes.colors.background_solid_neutral_secondary,
        },
      }}
    >
      <AppVersionUpdateModalContent
        canClose={canCloseUpgradeModal}
        onMaybeLater={() => closeModal()}
        onUpdateNow={handleUpdateNow}
      />
    </EdgenModal>
  );
};
